import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Card, Tag, Button, Input, Space, Modal, message, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, PhoneOutlined, PlusOutlined, SearchOutlined, HeartOutlined } from '@ant-design/icons';
import { 
  getVisibleMembers, 
  getMemberStoreName, 
  getMemberStaffName,
  categoryOptions,
  getCoupleTag
} from '../mockData';

const MemberList = ({ user }) => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [modalPoints, setModalPoints] = useState(0);

  // 获取当前用户可见的会员列表
  const allMembers = getVisibleMembers(user);

  // 根据夫妻关系排序
  const arrangeByCouple = (members) => {
    const sortedMembers = [...members];
    const coupleMap = new Map();
    
    // 按照夫妻关系构建映射
    sortedMembers.forEach(member => {
      if (member.coupleId) {
        if (!coupleMap.has(member.id)) {
          coupleMap.set(member.id, member);
        }
        if (!coupleMap.has(member.coupleId)) {
          const spouse = sortedMembers.find(m => m.id === member.coupleId);
          if (spouse) coupleMap.set(spouse.id, spouse);
        }
      }
    });
    
    // 创建最终排序后的数组
    const result = [];
    const processed = new Set();
    
    // 先处理夫妻
    sortedMembers.forEach(member => {
      if (processed.has(member.id)) return;
      
      result.push(member);
      processed.add(member.id);
      
      // 如果有配偶关系，立即将配偶添加到后面
      if (member.coupleId && !processed.has(member.coupleId)) {
        const spouse = sortedMembers.find(m => m.id === member.coupleId);
        if (spouse) {
          result.push(spouse);
          processed.add(spouse.id);
        }
      }
    });
    
    return result;
  };

  // 搜索过滤
  const filteredMembers = arrangeByCouple(
    allMembers.filter(member => {
      if (!searchText) return true;
      return (
        member.name.toLowerCase().includes(searchText.toLowerCase()) ||
        member.phone.includes(searchText) ||
        member.address.toLowerCase().includes(searchText.toLowerCase())
      );
    })
  );

  // 删除会员确认
  const handleDeleteConfirm = () => {
    message.success(`已删除会员: ${selectedMember.name}`);
    setDeleteModalVisible(false);
  };

  // 表格列定义
  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span>
          {text}
          {record.coupleId && (
            <Tag className="couple-tag" icon={<HeartOutlined />}>
              夫妻
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 80,
    },
    {
      title: '年龄',
      dataIndex: 'age',
      key: 'age',
      width: 80,
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => (
        <a 
          className="phone-link"
          href={`tel:${phone}`}
        >
          {phone} <PhoneOutlined style={{ marginLeft: 5 }} />
        </a>
      ),
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: '会员等级',
      dataIndex: 'category',
      key: 'category',
      render: (category) => {
        const colorMap = {
          'S': '#f50',
          'A': '#87d068',
          'B': '#2db7f5',
          'C': '#999',
        };
        return (
          <Tag color={colorMap[category] || '#999'}>
            {categoryOptions.find(item => item.value === category)?.label || category}
          </Tag>
        );
      },
    },
    {
      title: '积分',
      dataIndex: 'points',
      key: 'points',
    },
    {
      title: '所属门店',
      key: 'store',
      render: (_, record) => getMemberStoreName(record.id),
    },
    {
      title: '服务顾问',
      key: 'staff',
      render: (_, record) => getMemberStaffName(record.id),
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="修改会员">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/members/edit/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="删除会员">
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => {
                setSelectedMember(record);
                setDeleteModalVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 如果用户不是老板或店长，则不显示删除按钮
  if (user.role === 'staff') {
    columns.pop(); // 移除操作列
  }

  return (
    <>
      <Card 
        title="会员管理" 
        extra={
          ['boss', 'manager'].includes(user.role) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/members/add')}
            >
              添加会员
            </Button>
          )
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索会员姓名/电话/地址"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </div>
        
        <Table 
          dataSource={filteredMembers} 
          columns={columns} 
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* 删除确认对话框 */}
      <Modal
        title="删除会员"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteModalVisible(false)}
        okText="确认"
        cancelText="取消"
      >
        确定要删除会员 <strong>{selectedMember?.name}</strong> 吗？此操作不可恢复。
      </Modal>
    </>
  );
};

export default MemberList; 